import AddIcon from "@mui/icons-material/Add";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import ReorderIcon from "@mui/icons-material/Reorder";
import ViewStreamIcon from "@mui/icons-material/ViewStream";
import {
  Box,
  BoxProps,
  IconButton,
  InputBase,
  Typography,
} from "@mui/material";
import { Spacer, Toolbar } from "common";
import I18n from "i18n-js";
import { useState } from "react";
import FieldMenu from "./FieldMenu";
import { FieldCoordinates } from "./FormEditorDataProvider";
import Input from "./Input";
import { FieldItem } from "./types";

interface FieldBaseProps extends BoxProps {
  field: FieldItem;
  coordinates: FieldCoordinates;
  draghandlerProps?: any;
  mergedActions?: {
    blockActions?: any;
  };
}
export default function FieldBase({
  children,
  coordinates,
  field,
  draghandlerProps,
  mergedActions,
  ...rest
}: FieldBaseProps) {
  const [isEditingBlockName, setIsEditingBlockName] = useState(false);

  if (!field) {
    return null;
  }

  const hasMergedActions = !!mergedActions;
  const blockActions = mergedActions?.blockActions;
  return (
    <Box
      {...rest}
      sx={{
        margin: "2px",
        flex: 1,
        /*maxWidth:
          field.width && field.width !== 100 ? `${field.width}%` : "auto",
        flexBasis: field.width ? `${field.width}%` : undefined,
        flex: field.width ? undefined : 1,*/
        borderColor: "info.main",
        borderStyle: "dashed",
        backgroundColor: theme => theme.palette.background.default,
        borderWidth: 1,
        borderRadius: 1,
        padding: "3px",
        paddingTop: 0,
        ...rest.sx,
      }}
    >
      <Box>
        <Toolbar>
          <IconButton size="small" {...draghandlerProps}>
            <ReorderIcon fontSize="small" />
          </IconButton>

          {hasMergedActions && blockActions ? (
            // Show block name when actions are merged
            <>
              {blockActions.isEditingBlockName ? (
                <InputBase
                  size="small"
                  value={blockActions.blockName || ""}
                  autoFocus
                  sx={{
                    color: "primary.main",
                    flex: 1,
                    backgroundColor: "background.paper",
                    marginLeft: "4px",
                    "& input": {
                      padding: "2px 4px",
                    },
                  }}
                  onChange={evt =>
                    blockActions.setBlockName(
                      evt.target.value === "" ? null : evt.target.value
                    )
                  }
                  onBlur={() => {
                    blockActions.handleBlockChange(
                      { name: blockActions.blockName },
                      blockActions.blockIndex
                    );
                    blockActions.setIsEditingBlockName(false);
                  }}
                />
              ) : (
                <Toolbar
                  onClick={() => blockActions.setIsEditingBlockName(true)}
                  sx={{ cursor: "text", padding: 0 }}
                >
                  <Typography
                    color="primary.main"
                    variant="body2"
                    sx={{ marginLeft: 1 }}
                  >
                    {blockActions.blockName || "Untitled Block"}
                  </Typography>
                  <Spacer w={1} />
                  <EditIcon
                    fontSize="small"
                    sx={{ color: "primary.main", fontSize: "0.875rem" }}
                  />
                </Toolbar>
              )}
            </>
          ) : (
            <Typography color="purple" variant="caption">
              {field.name || I18n.t("FormEditor.field")}
            </Typography>
          )}

          <Spacer right />

          {/* Add field and add row actions when actions are merged */}
          {hasMergedActions && blockActions?.handleAddFields && (
            <IconButton
              title={I18n.t("FormEditor.addFieldToRowTitle")}
              size="small"
              onClick={() =>
                blockActions.handleAddFields(blockActions.blockIndex, 0)
              }
            >
              <AddIcon fontSize="small" />
            </IconButton>
          )}
          {hasMergedActions && blockActions?.handleAddRows && (
            <IconButton
              title={I18n.t("FormEditor.addNewRow")}
              size="small"
              onClick={() =>
                blockActions.handleAddRows(blockActions.blockIndex)
              }
            >
              <ViewStreamIcon fontSize="small" />
            </IconButton>
          )}
          <FieldMenu coordinates={coordinates} />
          {/* Block management actions - positioned at far right */}
          {hasMergedActions && blockActions && (
            <>
              <IconButton
                title={I18n.t("FormEditor.moveBlockUp")}
                size="small"
                onClick={() =>
                  blockActions.handleMoveBlockByDir(
                    blockActions.blockIndex,
                    "up"
                  )
                }
                disabled={blockActions.blockIndex < 1}
              >
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
              <IconButton
                title={I18n.t("FormEditor.moveBlockDown")}
                size="small"
                onClick={() =>
                  blockActions.handleMoveBlockByDir(
                    blockActions.blockIndex,
                    "down"
                  )
                }
                disabled={
                  blockActions.blockIndex >= blockActions.totalBlocks - 1
                }
              >
                <ArrowDownwardIcon fontSize="small" />
              </IconButton>
              <IconButton
                title={I18n.t("FormEditor.removeBlock")}
                size="small"
                onClick={() =>
                  blockActions.handleDeleteBlock(blockActions.blockIndex)
                }
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </>
          )}
        </Toolbar>
      </Box>
      <Input field={field} coordinates={coordinates} />
    </Box>
  );
}
